import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import reservationsWS from "../../services/wsReservations";

const TimeSelection = ({ onSelectionChange, roomId, isModalOpen, dateForHold, canCreateReservation = true }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState(null);
  const [endPosition, setEndPosition] = useState(null);
  const holdIdRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) {
      setStartPosition(null);
      setEndPosition(null);
    }
  }, [isModalOpen]);

  const calculateMinutes = (cellIndex, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percentageAcrossCell = relativeX / rect.width;
    // Each cell represents 30 minutes
    const minutesInCell = Math.round(percentageAcrossCell * 30);
    // Calculate total minutes from midnight
    return cellIndex * 30 + minutesInCell;
  };

  const handleMouseDown = (e, cellIndex) => {
    if (!canCreateReservation || !onSelectionChange) {
      if (!canCreateReservation) {
        console.log('Permission denied: User cannot create reservations for this resource');
      }
      return;
    }
    const minutes = calculateMinutes(cellIndex, e);
    setIsDragging(true);
    setStartPosition(minutes);
    setEndPosition(minutes);
    // announce hold start
    holdIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const date = dateForHold || dayjs().format("YYYY-MM-DD");
    reservationsWS.sendHold("hold_start", {
      hold_id: holdIdRef.current,
      date,
      resource_id: roomId,
      start_minute: minutes,
      end_minute: minutes,
    });
  };

  const handleMouseMove = (e, cellIndex) => {
    if (isDragging) {
      const minutes = calculateMinutes(cellIndex, e);
      setEndPosition(minutes);
      const date = dateForHold || dayjs().format("YYYY-MM-DD");
      reservationsWS.sendHold("hold_update", {
        hold_id: holdIdRef.current,
        date,
        resource_id: roomId,
        start_minute: Math.min(startPosition, minutes),
        end_minute: Math.max(startPosition, minutes),
      });
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      var start = Math.min(startPosition, endPosition);
      var end = Math.max(startPosition, endPosition);

      //   Round to 15 30 45
      start = Math.round(start / 15) * 15;
      end = Math.round(end / 15) * 15;

      if (end === start){
        end += 60;
      }

      // Convert minutes to HH:mm format
      const startTime = `${Math.floor(start / 60)
        .toString()
        .padStart(2, "0")}:${(start % 60).toString().padStart(2, "0")}`;
      const endTime = `${Math.floor(end / 60)
        .toString()
        .padStart(2, "0")}:${(end % 60).toString().padStart(2, "0")}`;

      onSelectionChange({
        roomId,
        startTime,
        endTime,
      });

      // announce hold end
      const date = dateForHold || dayjs().format("YYYY-MM-DD");
      reservationsWS.sendHold("hold_end", {
        hold_id: holdIdRef.current,
        date,
        resource_id: roomId,
        start_minute: start,
        end_minute: end,
      });
      holdIdRef.current = null;
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  const getBackgroundStyle = (cellIndex) => {
    if (startPosition === null || endPosition === null) return "inherit";

    const cellStartMinutes = cellIndex * 30;
    const cellEndMinutes = cellStartMinutes + 30;
    const selectionStart = Math.min(startPosition, endPosition);
    const selectionEnd = Math.max(startPosition, endPosition);

    // Check if this cell is within the selection range
    if (cellStartMinutes <= selectionEnd && cellEndMinutes >= selectionStart) {
      // Calculate the percentage of the cell that should be highlighted
      const startPercentage = Math.max(
        0,
        (selectionStart - cellStartMinutes) / 30
      );
      const endPercentage = Math.min(1, (selectionEnd - cellStartMinutes) / 30);

      return `linear-gradient(to right,
                transparent ${startPercentage * 100}%,
                color-mix(in oklab, var(--color-primary) 18%, transparent) ${startPercentage * 100}%,
                color-mix(in oklab, var(--color-primary) 18%, transparent) ${endPercentage * 100}%,
                transparent ${endPercentage * 100}%)`;
    }

    return "inherit";
  };

  return (
    <>
      {[...Array(48)].map((_, index) => (
        <div
          key={index}
          onMouseDown={(e) => handleMouseDown(e, index)}
          onMouseMove={(e) => handleMouseMove(e, index)}
          onMouseUp={handleMouseUp}
          style={{
            borderTop: "1px solid var(--border)",
            borderLeft: "none",
            background: getBackgroundStyle(index),
            cursor: canCreateReservation ? "pointer" : "not-allowed",
            height: "100%",
            userSelect: "none",
            opacity: canCreateReservation ? 1 : 0.6,
          }}
        />
      ))}
    </>
  );
};

export default TimeSelection;
